import type { WsChangesMessage } from '../../../shared/api/types';

export type WsMessageHandler = (message: WsChangesMessage) => void;
export type WsStatusHandler = (status: 'connecting' | 'open' | 'closed') => void;

export interface SyncWebSocketOptions {
  getWsUrl: () => string;
  onMessage: WsMessageHandler;
  onStatus: WsStatusHandler;
  onOpen?: () => void;
}

/**
 * WebSocket /ws: сервер только шлёт { type: 'changes', since }.
 * Клиент сам делает pull. Реконнект — снаружи через backoff.
 */
export class SyncWebSocket {
  private socket: WebSocket | null = null;
  private closedByClient = false;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly options: SyncWebSocketOptions) {}

  get isOpen(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  connect(): void {
    if (typeof WebSocket === 'undefined') {
      return;
    }
    if (this.socket !== null && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.closedByClient = false;
    this.options.onStatus('connecting');

    let socket: WebSocket;
    try {
      socket = new WebSocket(this.options.getWsUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket) {
        return;
      }
      this.reconnectAttempt = 0;
      this.options.onStatus('open');
      this.options.onOpen?.();
    };

    socket.onmessage = (event) => {
      if (this.socket !== socket) {
        return;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(String(event.data));
      } catch {
        return;
      }
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        (parsed as { type?: unknown }).type === 'changes' &&
        typeof (parsed as { since?: unknown }).since === 'number'
      ) {
        this.options.onMessage(parsed as WsChangesMessage);
      }
    };

    socket.onerror = () => {
      // onclose сработает после ошибки — здесь ничего не делаем
    };

    socket.onclose = () => {
      if (this.socket !== socket) {
        return;
      }
      this.socket = null;
      this.options.onStatus('closed');
      if (!this.closedByClient) {
        this.scheduleReconnect();
      }
    };
  }

  close(): void {
    this.closedByClient = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.socket;
    this.socket = null;
    if (socket !== null) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close();
      } catch {
        // сокет уже закрыт
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.closedByClient) {
      return;
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
    }
    const delay = Math.min(60_000, 1000 * 2 ** Math.min(this.reconnectAttempt, 6));
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}

export function resolveWsUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}
